import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Offcanvas,
  Spinner,
  Table,
} from "react-bootstrap";
import Link from "next/link";

import {
  CopyValue,
  FixedToolbarItem,
  GridForm,
  GridFormItemWrapper,
  Toolbar,
  useToastManager,
} from "@bagel-web/components";
import { CommonNewProject, CommonProject } from "../types/Project";

const isValidProjectName = (value: string) => value.trim().length > 0;

export const NewProjectDrawer = ({
  show,
  onClose,
  onSave,
}: {
  show: boolean;
  onClose: () => void;
  onSave: (p: CommonNewProject) => Promise<CommonProject>;
}) => {
  const { publishToast } = useToastManager();
  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const trimmedName = name.trim();

  const resetDrawerState = () => {
    setName("");
    setErrorMessage("");
  };

  const handleCancel = () => {
    resetDrawerState();
    onClose();
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onSave({ name: trimmedName });
      publishToast("Project created");
      resetDrawerState();
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Unknown error");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const saveDisabled = isSaving || !isValidProjectName(trimmedName);

  return (
    <Offcanvas show={show} placement="end" onHide={handleCancel}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>New Project</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
        <GridForm $numCols={1} $width="100%" onSubmit={handleSave}>
          <GridFormItemWrapper>
            <Form.Label>Project Name</Form.Label>
            <Form.Control
              autoFocus={true}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project name"
            />
          </GridFormItemWrapper>
        </GridForm>
        <Toolbar className="mt-3" style={{ justifyContent: "end" }}>
          <FixedToolbarItem>
            <Button
              variant="primary"
              disabled={saveDisabled}
              onClick={handleSave}
            >
              {isSaving ? <Spinner size="sm" /> : "Save"}
            </Button>
          </FixedToolbarItem>
        </Toolbar>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

const ProjectsTable = <T extends CommonProject>({
  projects = [],
  currentProjectId,
  getProjectLink,
  showSwitchProjectsLink,
}: {
  projects?: Array<T>;
  currentProjectId?: string | null;
  getProjectLink?: (projectId: string) => string;
  showSwitchProjectsLink?: (project: T) => boolean;
}) => {
  return (
    <>
      <Table hover>
        <thead>
          <tr>
            <th>Project</th>
            <th>ID</th>
            {showSwitchProjectsLink && <th />}
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => {
            const isCurrentProject = currentProjectId === p.id;
            const link = getProjectLink ? getProjectLink(p.id) : null;
            return (
              <tr key={p.id} className={"align-middle"}>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    {link ? <Link href={link}>{p.name}</Link> : p.name}
                    {p.archived_at !== null && (
                      <Badge bg="danger">Archived</Badge>
                    )}
                  </div>
                </td>
                <td>
                  <CopyValue>{p.id}</CopyValue>
                </td>
                {showSwitchProjectsLink && (
                  <td>
                    {p.archived_at === null &&
                      !isCurrentProject &&
                      showSwitchProjectsLink(p) && (
                        <Link href={`/switch-project/${p.id}?path=/projects`}>
                          Switch to Project
                        </Link>
                      )}
                    {isCurrentProject && (
                      <Badge bg="dark">Current Project</Badge>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
      {projects.length === 0 && (
        <p className="text-muted text-center mt-5">
          Create a project to get started.
        </p>
      )}
    </>
  );
};

export default ProjectsTable;
